import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { fetchLiveHits, parseHits, parsePlayerHitLines } from '../../api/hitTracker';
import HitSummaryBar from './HitSummaryBar';
import HitFeed from './HitFeed';
import HitTypeChart from './HitTypeChart';
import PlayerHitLines from './PlayerHitLines';
import HRHighlight from './HRHighlight';
import PlayerProfile from '../PlayerProfile';

const { FiRefreshCw, FiZap, FiTarget, FiList, FiBarChart2, FiStar } = FiIcons;

const TABS = [
  { id: 'feed',    label: 'Live Feed',    icon: FiZap },
  { id: 'homers',  label: 'Home Runs',    icon: FiStar },
  { id: 'lines',   label: 'Batting Lines', icon: FiList },
  { id: 'chart',   label: 'Hit Chart',    icon: FiBarChart2 },
];

const REFRESH_INTERVAL = 20000;

const InGameHitTracker = ({ game }) => {
  const [hits, setHits] = useState([]);
  const [playerLines, setPlayerLines] = useState({ away: [], home: [] });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tab, setTab] = useState('feed');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isLive, setIsLive] = useState(false);

  const gamePk = game?.gamePk;
  const awayAbbr = game?.teams?.away?.team?.abbreviation || 'AWY';
  const homeAbbr = game?.teams?.home?.team?.abbreviation || 'HME';
  const gameState = game?.status?.abstractGameState;

  const load = useCallback(async () => {
    if (!gamePk) return;
    try {
      const { playByPlay, boxScore } = await fetchLiveHits(gamePk);
      setHits(parseHits(playByPlay?.allPlays || []));
      setPlayerLines(parsePlayerHitLines(boxScore));
      setLastUpdated(new Date());
      setIsLive(gameState === 'Live');
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [gamePk, gameState]);

  useEffect(() => {
    load();
    if (gameState === 'Live') {
      const id = setInterval(load, REFRESH_INTERVAL);
      return () => clearInterval(id);
    }
  }, [load, gameState]);

  if (!gamePk || gameState === 'Preview') return null;

  const homeRuns = hits.filter(h => h.event === 'Home Run');

  return (
    <>
      <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.05)] w-full">
        {/* Header */}
        <div className="p-3 sm:p-5 border-b-2 border-[#658d71] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiTarget} size={18} className="text-[#a9c9b1]" />
            <div>
              <h3 className="text-base sm:text-xl font-black tracking-widest text-[#d8ceb3] uppercase">
                In-Game Hit Tracker
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                {isLive && (
                  <span className="flex items-center gap-1 text-[9px] text-[#c4333e] font-black uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#c4333e] animate-pulse" />
                    Live · Auto-refresh 20s
                  </span>
                )}
                {!isLive && lastUpdated && (
                  <span className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest">
                    Final · {hits.length} hits recorded
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest hidden sm:block">
                {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1.5 border border-[#658d71] text-[#a9c9b1] hover:border-[#a9c9b1] hover:text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} size={12} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Bar */}
        {!loading && hits.length > 0 && (
          <div className="px-3 sm:px-5 pt-4">
            <HitSummaryBar hits={hits} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b-2 border-[#658d71] mt-4 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 sm:px-5 py-2.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest whitespace-nowrap border-r border-[#658d71] last:border-0 transition-colors ${
                tab === t.id
                  ? 'bg-[#386646] text-[#d8ceb3]'
                  : 'text-[#a9c9b1] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'
              }`}
            >
              <SafeIcon icon={t.icon} size={11} />
              {t.label}
              {t.id === 'homers' && homeRuns.length > 0 && (
                <span className="ml-1 bg-[#c4333e] text-white text-[8px] font-black px-1.5 py-0.5">
                  {homeRuns.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-3 sm:p-5">
          {loading ? (
            <div className="flex items-center justify-center py-14 gap-3">
              <div className="w-7 h-7 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
              <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading hits...</span>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
              >
                {tab === 'feed' && (
                  <HitFeed
                    hits={hits}
                    awayAbbr={awayAbbr}
                    homeAbbr={homeAbbr}
                    onPlayerClick={(id, name) => setSelectedPlayer({ id, name })}
                  />
                )}
                {tab === 'homers' && (
                  <HRHighlight
                    homeRuns={homeRuns}
                    awayAbbr={awayAbbr}
                    homeAbbr={homeAbbr}
                    onPlayerClick={(id, name) => setSelectedPlayer({ id, name })}
                  />
                )}
                {tab === 'lines' && (
                  <PlayerHitLines
                    playerLines={playerLines}
                    awayAbbr={awayAbbr}
                    homeAbbr={homeAbbr}
                    onPlayerClick={(id, name) => setSelectedPlayer({ id, name })}
                  />
                )}
                {tab === 'chart' && (
                  <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
                    <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
                      <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">
                        Hit Type Distribution
                      </span>
                    </div>
                    <div className="p-3">
                      <HitTypeChart hits={hits} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {selectedPlayer && (
        <PlayerProfile
          playerId={selectedPlayer.id}
          playerName={selectedPlayer.name}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </>
  );
};

export default InGameHitTracker;